// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import constants from "@store/constants";
import { TECertificateStateContext } from "@store/Framework/Store/Greif/Data/TaxExemptionCertificates/TaxExemptionCertificatesSelectors";
import TaxExemptionCertificateForm from "@store/Library/Components/TaxExemptionCertificateForm";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { useContext } from "react";
import { connect } from "react-redux";

const enum fields {
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    fileImportFormat = "fileImportFormat",
    financeCompanyRequired = "financeCompanyRequired",
    messageRequired = "messageRequired",
    firstNameRequired = "firstNameRequired",
    lastNameRequired = "lastNameRequired",
    fileRequired = "fileRequired",
    allowMultipleFiles = "allowMultipleFiles",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.fileImportFormat]: string;
        [fields.financeCompanyRequired]: boolean;
        [fields.messageRequired]: boolean;
        [fields.firstNameRequired]: boolean;
        [fields.lastNameRequired]: boolean;
        [fields.fileRequired]: boolean;
        [fields.allowMultipleFiles]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    queryParams: parseQueryString<{ returnPage?: string }>(getLocation(state).search),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface SubmitNewTECFormStyles {
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

const tecContactSupportFormStyles: SubmitNewTECFormStyles = {
    noResultsText: typographyGetNoResultsAltProps({ spaced: true }),
};

const styles = tecContactSupportFormStyles;

const SubmitNewTECForm = ({
    fields: {
        fileImportFormat,
        successMessage,
        submitButtonText,
        messageRequired,
        firstNameRequired,
        lastNameRequired,
        financeCompanyRequired,
        fileRequired,
        allowMultipleFiles = false,
    },
    queryParams: { returnPage },
}: Props) => {
    const certificateDataView = usePersistentDataView(useContext(TECertificateStateContext));
    const certificateData = certificateDataView?.value;

    const linkBack = returnPage
        ? usePageLinkByPageType(returnPage)
        : usePageLinkByPageType(constants.pageType.taxExemptionCertificates);

    return (
        <>
            {!certificateData ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText}>{translate("No certificate found")}</Typography>
                </StyledWrapper>
            ) : (
                <TaxExemptionCertificateForm
                    id="tecContactSupportForm"
                    formVariant="support"
                    fileImportFormat={fileImportFormat}
                    messageRequired={messageRequired}
                    firstNameRequired={firstNameRequired}
                    lastNameRequired={lastNameRequired}
                    financeCompanyRequired={financeCompanyRequired}
                    fileRequired={fileRequired}
                    allowMultipleFiles={allowMultipleFiles}
                    successMessage={successMessage}
                    submitButtonText={submitButtonText}
                    certificateData={certificateData}
                    linkBack={linkBack}
                />
            )}
        </>
    );
};

const fieldsTab = {
    displayName: "Fields",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SubmitNewTECForm),
    definition: {
        displayName: "TEC Contact Form",
        group: constants.widgetGroup.tecContactSupport,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
            {
                name: fields.fileImportFormat,
                displayName: "Allowed File Formats",
                fieldType: "General",
                editorTemplate: "TextField",
                tooltip: "Use comma-separated values. i.e.: .pdf, .zip",
                defaultValue: ".pdf, .zip",
            },
            {
                name: fields.allowMultipleFiles,
                displayName: "Allow Multiple Files to be Submitted",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.financeCompanyRequired,
                displayName: "Greif Finance Company Required",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: fieldsTab,
            },
            {
                name: fields.messageRequired,
                displayName: "Message Required",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: fieldsTab,
            },
            {
                name: fields.firstNameRequired,
                displayName: "First Name Required",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: fieldsTab,
            },
            {
                name: fields.lastNameRequired,
                displayName: "Last Name Required",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: fieldsTab,
            },
            {
                name: fields.fileRequired,
                displayName: "File Required",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: fieldsTab,
            },
        ],
        allowedContexts: ["TECContactSupportPage" as any],
    },
};

export default widgetModule;
